import { ExportFormat, LayerOrigin, SelectionModeType } from "../../interfaces";
import { ImageLoader, ObjectId } from "../../helpers";
import {
  ActiveSelection,
  FabricCanvas,
  FabricImage,
  FabricObject,
  FabricPoint,
  FabricRect,
  TEvent,
  TPointerEvent,
} from "../../objects";
import {
  centerCanvasViewport,
  createImageObject,
  createLayer,
  focusObject,
  insertAt,
  layersToTarget,
  pasteLayers,
  sortObjectsByZIndex,
  updateActiveSelection,
  updateObjectPosition,
} from "../../utils";
import Layer, { LayerProps } from "../Layer";
import Group from "../Group";
import { CanvasState, LayerAction, LayerSnapshot } from "./interfaces";

export default class Canvas extends FabricCanvas {
  _id: string = new ObjectId().toString();
  clipPath: FabricRect = new FabricRect();
  selectionMode: SelectionModeType = SelectionModeType.Single;
  layerOrigin: LayerOrigin = { originX: "left", originY: "top" };
  lastActiveGroup?: Group;

  zoomPoint: FabricPoint = new FabricPoint(0, 0);

  pannable: boolean = false;
  panPoint: FabricPoint = new FabricPoint(0, 0);
  previousX: number = 0;
  previousY: number = 0;

  clonedLayerObjects: LayerProps[] = [];

  currentState?: CanvasState;
  undoStack: CanvasState[] = [];
  redoStack: CanvasState[] = [];

  isLoading: boolean = false;

  requestRenderAll() {
    if (this.isLoading) return this;
    return super.requestRenderAll();
  }

  groupMap: Map<string, Group> = new Map();
  layerSnapshotMap: Map<string, LayerSnapshot> = new Map();

  layerPropsList: any[] = [];

  constructor(element?: string | HTMLCanvasElement, options?: object) {
    super(element, options);
    this.on("mouse:wheel", this.mouseWheelSettings);
    this.on("mouse:down", this.startPanning);
    this.on("selection:created", this.onSelectionCreated);
    this.on("selection:updated", this.onSelectionCreated);
    this.on("object:modified", this.onLayerModified);
    this.on("group:focus", this.onFocusGroup);
  }

  remove(...objects: Layer[]) {
    objects.forEach((o) => {
      o.groupObject?.remove(o);
    });
    super.remove(...objects);
    return objects;
  }

  getLayers() {
    return this.getObjects().filter((o) => o instanceof Layer);
  }

  createGroup(layers: Layer[]) {
    const objects = this.getObjects();
    const objToIdxMap = new Map<FabricObject, number>(
      objects.map((o, i) => [o, i]),
    );

    const layersToGroup: Layer[] = [];
    const groups: (Group | undefined)[] = [];
    layers.forEach((l) => {
      layersToGroup.push(l);
      groups.push(l.groupObject);
    });
    const uniqueGroups = [...Array.from(new Set(groups))];
    if (layersToGroup.length === 0 || uniqueGroups.length > 1) return;

    layersToGroup.sort((l1, l2) => objToIdxMap.get(l1)! - objToIdxMap.get(l2)!);

    const lastLayer = layersToGroup[layersToGroup.length - 1];
    const insertIdx = objToIdxMap.get(lastLayer);
    if (insertIdx === undefined) return;
    layersToGroup.forEach((l) => this.moveObjectTo(l, insertIdx));

    const currentGroup = uniqueGroups[0];
    const group = new Group(layersToGroup, {
      canvas: this,
      parentObject: currentGroup,
    });
    if (currentGroup) {
      const index = currentGroup.objects.indexOf(lastLayer);
      currentGroup.objects = insertAt(currentGroup.objects, index, group);
      currentGroup.remove(...layersToGroup);
    }

    this.groupMap.set(group._id, group);
    this.fire("group:created", { target: group });

    setTimeout(() => {
      const activeObject = group.getSelection();
      this.setActiveObject(activeObject);
      this.requestRenderAll();
    });
  }

  getGroups() {
    const groupSet: Set<Group> = new Set();
    this.getLayers().forEach((layer) => {
      let groupObject = layer.groupObject;
      while (groupObject && !groupSet.has(groupObject)) {
        groupSet.add(groupObject);
        groupObject = groupObject.parentObject;
      }
    });
    return [...Array.from(groupSet)];
  }

  setViewportSize(
    size: Partial<{ width: number; height: number }>,
    centered: boolean = false,
  ) {
    this.clipPath.set(size);
    if (centered) {
      centerCanvasViewport(this, "contain");
    }
    this.fire("clippath:modified");
    this.requestRenderAll();
  }

  async loadBackgroundImage(src?: string | Blob) {
    src = src instanceof Blob ? URL.createObjectURL(src) : src;
    if (src) {
      const backgroundImage =
        (this.backgroundImage as FabricImage) || createImageObject();
      try {
        const imageElement = await ImageLoader.load(src);
        if (!imageElement) return;
        backgroundImage.setElement(imageElement);
        this.set({ backgroundImage });
        this.setViewportSize(
          {
            width: backgroundImage.width,
            height: backgroundImage.height,
          },
          true,
        );
      } catch {
        this.set({ backgroundImage: undefined });
      }
    } else {
      this.set({ backgroundImage: undefined });
    }
    this.requestRenderAll();
    this.fire("background:loaded");
  }

  mouseWheelSettings(event: TEvent<WheelEvent>) {
    event.e.preventDefault();
    event.e.stopPropagation();

    const zoomPoint = this.zoomPoint;
    const zoomLevel = this.getZoom();

    const viewportWidth = this?.clipPath?.width || this.width;
    const viewportHeight = this?.clipPath?.height || this.height;
    const zoomDelta = zoomLevel * 0.04;

    const point = {
      x: this.width / 2,
      y: this.height / 2,
    };
    zoomPoint.setXY(point.x, point.y);
    if (event.e.deltaY < 0) {
      this.zoomToPoint(zoomPoint, zoomLevel + zoomDelta);
    } else {
      if (zoomLevel * viewportWidth > 100 && zoomLevel * viewportHeight > 100) {
        this.zoomToPoint(zoomPoint, zoomLevel - zoomDelta);
      }
    }
  }

  startPanning(event: TEvent<TPointerEvent>) {
    if (this.pannable) {
      const pe: MouseEvent = event.e as MouseEvent;
      this.previousX = pe.clientX;
      this.previousY = pe.clientY;
      this.on("mouse:move", this.onPanning);
      this.on("mouse:up", this.onEndPanning);
    }
  }

  onPanning(event: TEvent<TPointerEvent>) {
    const pe: MouseEvent = event.e as MouseEvent;
    const x = pe.clientX - this.previousX;
    const y = pe.clientY - this.previousY;
    this.previousX = pe.clientX;
    this.previousY = pe.clientY;
    this.panPoint.setXY(x, y);
    this.relativePan(this.panPoint);
    this.fire("clippath:modified");
  }

  onEndPanning() {
    this.off("mouse:move", this.onPanning);
    this.off("mouse:up", this.onEndPanning);
  }

  onFocusGroup(event: any) {
    const targetGroup: Group = event.target;
    const layers = this.getLayers();

    this.groupMap.forEach((g) => {
      if (g === targetGroup) g.fire("selected");
      else g.fire("deselected");
    });

    if (!targetGroup) return;
    this.lastActiveGroup = targetGroup;
    const layersToVisibleSet = new Set(targetGroup.getLayers());

    for (let i = 0; i < layers.length; i++) {
      const l = layers[i];
      if (!l.groupObject) continue;
      const visible = layersToVisibleSet.has(l);
      focusObject(l, visible);
    }

    this.requestRenderAll();
  }

  onSelectionCreated() {
    const activeObject = this.getActiveObject();
    if (activeObject instanceof ActiveSelection) {
      updateActiveSelection(activeObject);
      updateObjectPosition(activeObject);
    }
  }

  onLayerModified(event?: any) {
    const actionToUndoSet = new Set<string>(Object.values(LayerAction));
    const action = event?.action;
    if (!action || !actionToUndoSet.has(action)) return;

    const layer = event?.target;
    const modifiedLayers: LayerProps[] = [];
    setTimeout(async () => {
      if (layer instanceof ActiveSelection) {
        updateObjectPosition(layer);
        const layers = layer.getObjects().filter((o) => o instanceof Layer);

        // old
        layer.removeAll();
        layers.forEach((l) => {
          l.fire("modified");
          modifiedLayers.push(l.toObject());
        });

        // new
        // layer.removeAll();
        // const layerObjects = await processLayersInBatches(layers, 20);
        // modifiedLayers.push(...layerObjects);
        // rollback if any error

        const activeSelection = layersToTarget(layers);
        if (action !== "remove") this.setActiveObject(activeSelection);
        this.requestRenderAll();
      } else if (layer instanceof Layer) {
        modifiedLayers.push(layer.toObject());
        if (action !== "remove") this.setActiveObject(layer);
        this.requestRenderAll();
      }
      this.saveState(modifiedLayers, action as LayerAction);
    });
  }

  saveState(modifiedLayers: LayerProps[], action?: LayerAction) {
    if (this.currentState) {
      if (modifiedLayers.length === 0) return;
      this.undoStack.push(this.currentState);

      const currentLayers = this.currentState.layers;
      const currentLayerIds = new Set(currentLayers.map((layer) => layer._id));
      const modifiedLayerMap = new Map(
        modifiedLayers.map((ml) => [ml._id, ml]),
      );

      const layers: LayerProps[] = [];
      if (action === "remove") {
        const removedLayerIds = modifiedLayers.map((ml) => ml._id);
        const updatedLayers = currentLayers.filter(
          (layer) => !removedLayerIds.includes(layer._id),
        );
        layers.push(...updatedLayers);
      } else {
        const newAddedLayers = modifiedLayers.filter(
          (ml) => !currentLayerIds.has(ml._id),
        );
        const updatedLayers = currentLayers.map((layer) => {
          const modifiedLayer = modifiedLayerMap.get(layer._id);
          return modifiedLayer || layer;
        });
        layers.push(...updatedLayers, ...newAddedLayers);
      }

      const newState = {
        ...this.currentState,
        layers,
      };

      this.currentState = newState;
    } else {
      const { backgroundColor, backgroundImagePath, libraryItemId, clipPath } =
        this;
      const newState = {
        backgroundColor: backgroundColor as string,
        backgroundImagePath,
        libraryItemId,
        width: clipPath.width,
        height: clipPath.height,
        layers: this.getLayers().map((l) => l.toObject()),
      };
      this.currentState = newState;
    }
  }

  updateState(state: CanvasState) {
    this.discardActiveObject();
    const layers = this.getLayers();
    const { width, height, layers: layerObjects } = state;
    this.setViewportSize({ width, height });

    const layerMap = new Map(layers.map((l) => [l._id, l]));
    const layerObjectMap = new Map(
      layerObjects.map((prop) => [prop._id, prop]),
    );

    layers.forEach((layer) => {
      const layerObject = layerObjectMap.get(layer._id);
      if (layerObject) {
        const { x, y, width, height, angle } = layerObject;
        layer.set({ width, height, angle });
        layer.setPosition({ x, y });
        layer.setCoords();
        layer.fire("modified");
      } else {
        this.remove(layer);
      }
    });

    layerObjects.forEach((obj: LayerProps, index) => {
      if (!layerMap.has(obj._id)) {
        const newLayer = createLayer(obj);
        this.insertAt(index, newLayer);
      }
    });

    this.requestRenderAll();
  }

  undo() {
    const stateToUndo = this.undoStack.pop();
    if (stateToUndo) {
      if (this.currentState) {
        this.redoStack.push(this.currentState);
      }
      this.currentState = stateToUndo;
      this.updateState(stateToUndo);
    }
  }

  redo() {
    const stateToRedo = this.redoStack.pop();
    if (stateToRedo) {
      if (this.currentState) {
        this.undoStack.push(this.currentState);
      }
      this.currentState = stateToRedo;
      this.updateState(stateToRedo);
    }
  }

  onKeyDown(event: KeyboardEvent) {
    const selectionString = window.getSelection()?.toString();
    const activeElement = document.activeElement;
    const activeElementTagName = activeElement?.tagName.toLowerCase() || "";
    if (["textarea", "input"].includes(activeElementTagName)) return;

    const modifierKeyPressed = event.ctrlKey || event.metaKey;
    if (modifierKeyPressed) this.selectionMode = SelectionModeType.Multiple;
    if (event.shiftKey) this.selectionMode = SelectionModeType.Range;

    switch (event.key) {
      case "Delete": {
        const layers = (this.getActiveObjects() as Layer[]) || [];
        if (layers.length === 0) return;
        const layersToRemove = layers.filter((l) => l.removable);
        this.remove(...layersToRemove);
        this.discardActiveObject();
        this.requestRenderAll();

        const target = layersToTarget(layersToRemove);
        this.fire("object:modified", { action: "remove", target: target });
        return;
      }
      case " ": {
        this.pannable = true;
        this.fire("panning");
        return;
      }
      case "c": {
        if (selectionString) return;
        if (modifierKeyPressed) {
          const layers = sortObjectsByZIndex(this.getActiveObjects());
          const clonedLayerObjects = layers.map((layer) => layer.toObject());
          this.clonedLayerObjects = clonedLayerObjects;
        }
        return;
      }
      case "v": {
        if (modifierKeyPressed) pasteLayers(this, this.clonedLayerObjects);
        return;
      }
      case "z": {
        if (modifierKeyPressed) this.undo();
        return;
      }
      case "y": {
        if (modifierKeyPressed) this.redo();
        return;
      }
      case "a": {
        if (modifierKeyPressed) {
          const layers = this.getLayers().filter((l) => !l.locked);
          const activeSelection = layersToTarget(layers);
          this.setActiveObject(activeSelection);
          this.requestRenderAll();
        }
        return;
      }

      default: {
        let dx = 0;
        let dy = 0;

        switch (event.key) {
          case "ArrowUp":
            dy = -1;
            break;
          case "ArrowDown":
            dy = 1;
            break;
          case "ArrowLeft":
            dx = -1;
            break;
          case "ArrowRight":
            dx = 1;
            break;
        }

        if (dx !== 0 || dy !== 0) {
          const layer = this.getActiveObject() as Layer;
          if (layer.locked) return;

          const { originX, originY } = this.layerOrigin;
          const { x, y } = layer.getPointByOrigin(originX, originY);
          layer.setPosition({ x: x + dx, y: y + dy });
          layer.fire("modified");

          this.requestRenderAll();
          this.fire("object:modified", { action: "drag", target: layer });
          return;
        }
      }
    }
  }

  onKeyUp(event: KeyboardEvent) {
    const activeElement = document.activeElement;
    const activeElementTagName = activeElement?.tagName.toLowerCase() || "";
    if (["textarea", "input"].includes(activeElementTagName)) return;

    const modifierKeyPressed = event.ctrlKey || event.metaKey;
    if (!modifierKeyPressed && !event.shiftKey) {
      this.selectionMode = SelectionModeType.Single;
    } else if (modifierKeyPressed) {
      this.selectionMode = SelectionModeType.Multiple;
    } else if (event.shiftKey) {
      this.selectionMode = SelectionModeType.Range;
    }

    switch (event.key) {
      case " ": {
        this.pannable = false;
        this.fire("panning");
        return;
      }
      default: {
      }
    }
  }

  onBlur() {
    this.selectionMode = SelectionModeType.Single;
  }
}

export * from "./interfaces";
